import { isValidPhoneNumber } from 'libphonenumber-js';
import { z } from 'zod';

const MAX_REGISTER_EMAIL_LENGTH = 150;
const MAX_REGISTER_NAME_LENGTH = 150;
const MAX_REGISTER_PHONE_LENGTH = 50;
const MAX_REGISTER_WEBSITE_LENGTH = 2000;

/**
 * Returns whether `raw` is a plausible company website URL.
 *
 * Empty or whitespace-only is treated as valid so optional fields can stay blank.
 * Uses the URL parser (same as the browser) instead of a regex: handles IDN, ports,
 * paths, and query strings without maintaining a fragile pattern.
 * Only `http:` and `https:` are allowed. If there is no scheme, `https://` is assumed
 * for parsing so values like `example.com` are accepted.
 * Hostnames must contain a dot between labels (e.g. example.com). A trailing dot
 * alone (google.) is stripped first per DNS FQDN rules so it does not count as a TLD.
 */
function isValidCompanyWebsiteUrl(raw: string): boolean {
  const trimmed = raw.trim();

  if (trimmed === '') return true;

  const withScheme = /^https?:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;

  let url: URL;

  try {
    url = new URL(withScheme);
  } catch {
    return false;
  }

  if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;

  // Trailing "." is valid in URLs but is not a second label; strip before requiring a dot.
  const host = url.hostname.replace(/\.+$/, '');

  if (!host || host.includes(' ')) return false;
  if (!host.includes('.')) return false;

  return true;
}

export const registerSchema = z.object({
  email: z
    .string()
    .max(
      MAX_REGISTER_EMAIL_LENGTH,
      `Email must be ${MAX_REGISTER_EMAIL_LENGTH} characters or fewer`
    )
    .email('Invalid email format'),
  first_name: z
    .string()
    .max(
      MAX_REGISTER_NAME_LENGTH,
      `First name must be ${MAX_REGISTER_NAME_LENGTH} characters or fewer`
    )
    .optional(),
  last_name: z
    .string()
    .max(
      MAX_REGISTER_NAME_LENGTH,
      `Last name must be ${MAX_REGISTER_NAME_LENGTH} characters or fewer`
    )
    .optional(),
  phone_number: z.string().optional(),
  company_name: z.string().optional(),
  company_website: z.string().optional(),
  monthly_revenue: z.string().optional(),
  job_title: z.string().optional(),
  agreed_to_terms: z.boolean().refine((val) => val === true, {
    message: 'You must agree to the Terms of Service'
  })
});

export const openRegisterSchema = registerSchema.extend({
  first_name: z
    .string()
    .nonempty('First name is required')
    .max(
      MAX_REGISTER_NAME_LENGTH,
      `First name must be ${MAX_REGISTER_NAME_LENGTH} characters or fewer`
    ),
  last_name: z
    .string()
    .nonempty('Last name is required')
    .max(
      MAX_REGISTER_NAME_LENGTH,
      `Last name must be ${MAX_REGISTER_NAME_LENGTH} characters or fewer`
    ),
  phone_number: z
    .string()
    .nonempty('Phone number is required')
    .max(
      MAX_REGISTER_PHONE_LENGTH,
      `Phone number must be ${MAX_REGISTER_PHONE_LENGTH} characters or fewer`
    )
    .refine((value) => isValidPhoneNumber(value, { defaultCountry: 'US' }), {
      message: 'Please enter a valid phone number'
    }),
  company_website: z
    .string()
    .max(
      MAX_REGISTER_WEBSITE_LENGTH,
      `Company website must be ${MAX_REGISTER_WEBSITE_LENGTH} characters or fewer`
    )
    .refine((val) => isValidCompanyWebsiteUrl(val), {
      message: 'Please enter a valid website URL'
    }),
  monthly_revenue: z.string().nonempty('Please select an option')
});

export type RegisterFormValues = z.infer<typeof registerSchema>;
